/**
 * RedeemableItemsAdmin
 *
 * Owner-side CRUD for `businessListings/{businessId}/redeemableItems/{itemId}`.
 * Items are what travellers spend tokens on at this business.
 *
 * Item shape:
 *   { name, description, tokenCost, totalInventory|null, reservedCount,
 *     redeemedCount, maxRedemptionsPerUser|null, requiresStaffConfirmation,
 *     enabled, visibleFrom|null, visibleUntil|null,
 *     createdAt, updatedAt, displayOrder }
 *
 * Writes are direct Firestore (rules allow owner). Spend transactions are
 * handled server-side and update reservedCount/redeemedCount atomically.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  where,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faCoins,
  faToggleOn,
  faToggleOff,
  faExclamationTriangle,
  faSpinner,
  faSave,
  faTimes,
  faCalendarAlt,
  faUndo,
  faClockRotateLeft,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';

import { firestore, functions } from '../../FirebaseConfig';
import { useToast } from '../ui/Toast';
import { HelpTip } from '../ui/HelpTip';
import ConfirmDialog from '../ui/ConfirmDialog/ConfirmDialog';
import {
  listEvents,
  linkRedeemableToEvent,
  unlinkRedeemableFromEvent,
} from '../../utils/events';
import '../../styles/RedeemableItemsAdmin.css';

const EMPTY_ITEM = {
  name: '',
  description: '',
  tokenCost: 1,
  totalInventory: null,
  maxRedemptionsPerUser: null,
  requiresStaffConfirmation: true,
  enabled: true,
  visibleFrom: null,
  visibleUntil: null,
  displayOrder: 0,
  eventId: null,
};

function tsToInputValue(ts) {
  if (!ts) return '';
  const ms = ts.toMillis ? ts.toMillis() : ts;
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inputValueToTs(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

function RedeemableItemsAdmin({ businessId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, ...item}
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Recent redemptions (live) + refund flow
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [refundTarget, setRefundTarget] = useState(null); // redemption row
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  // Business events (for the "Link to Event" picker on each redeemable).
  const [businessEvents, setBusinessEvents] = useState([]);
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const evs = await listEvents({
          businessId,
          statusFilter: null,
          upcomingOnly: false,
          limit: 100,
        });
        if (!cancelled) setBusinessEvents(evs || []);
      } catch (err) {
        console.warn('[RedeemableItemsAdmin] could not load events for picker', err && err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  // ----- Subscribe to items collection -----
  useEffect(() => {
    if (!businessId) return undefined;
    const colRef = collection(firestore, 'businessListings', businessId, 'redeemableItems');
    const q = query(colRef, orderBy('displayOrder', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[RedeemableItemsAdmin] snapshot failed', err);
        toast.error('Failed to load redeemable items.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [businessId, toast]);

  // ----- Subscribe to recent redemptions for this business -----
  useEffect(() => {
    if (!businessId) return undefined;
    const q = query(
      collection(firestore, 'redemptions'),
      where('businessId', '==', businessId),
      where('status', 'in', ['confirmed', 'refunded']),
      orderBy('confirmedAt', 'desc'),
      limit(25),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRecent(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRecentLoading(false);
      },
      (err) => {
        console.warn('[RedeemableItemsAdmin] recent snapshot failed', err && err.message);
        setRecentLoading(false);
      },
    );
    return () => unsub();
  }, [businessId]);

  // ----- Editor handlers -----
  const startNew = () => {
    setEditing({ ...EMPTY_ITEM, displayOrder: items.length });
  };

  const startEdit = (item) => {
    setEditing({ ...item });
  };

  const cancelEdit = () => setEditing(null);

  const setField = (patch) => setEditing((cur) => (cur ? { ...cur, ...patch } : cur));

  const validate = (e) => {
    if (!e.name || !e.name.trim()) return 'Name is required.';
    if (typeof e.tokenCost !== 'number' || e.tokenCost <= 0) return 'Token cost must be greater than zero.';
    if (e.totalInventory !== null && (typeof e.totalInventory !== 'number' || e.totalInventory < 0)) {
      return 'Total inventory must be a non-negative number, or blank for unlimited.';
    }
    if (e.maxRedemptionsPerUser !== null && (typeof e.maxRedemptionsPerUser !== 'number' || e.maxRedemptionsPerUser < 0)) {
      return 'Max per user must be a non-negative number, or blank for unlimited.';
    }
    return null;
  };

  const save = async () => {
    if (!editing) return;
    const err = validate(editing);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const colRef = collection(firestore, 'businessListings', businessId, 'redeemableItems');
      const isNew = !editing.id;
      const previousEventId = !isNew
        ? (items.find((it) => it.id === editing.id)?.eventId || null)
        : null;
      const nextEventId = editing.eventId || null;
      const payload = {
        name: editing.name.trim(),
        description: (editing.description || '').trim(),
        tokenCost: Number(editing.tokenCost),
        totalInventory: editing.totalInventory === null ? null : Number(editing.totalInventory),
        maxRedemptionsPerUser: editing.maxRedemptionsPerUser === null ? null : Number(editing.maxRedemptionsPerUser),
        requiresStaffConfirmation: !!editing.requiresStaffConfirmation,
        enabled: !!editing.enabled,
        visibleFrom: editing.visibleFrom || null,
        visibleUntil: editing.visibleUntil || null,
        displayOrder: Number(editing.displayOrder) || 0,
        eventId: nextEventId,
        updatedAt: serverTimestamp(),
      };
      let savedItemId;
      if (isNew) {
        const newDoc = doc(colRef);
        savedItemId = newDoc.id;
        await setDoc(newDoc, {
          ...payload,
          reservedCount: 0,
          redeemedCount: 0,
          createdAt: serverTimestamp(),
        });
      } else {
        savedItemId = editing.id;
        const ref = doc(firestore, 'businessListings', businessId, 'redeemableItems', editing.id);
        await updateDoc(ref, payload);
      }
      // Keep events.linkedRedeemableIds in sync. Best-effort — UI failures
      // here should not block the primary save.
      try {
        if (previousEventId && previousEventId !== nextEventId) {
          await unlinkRedeemableFromEvent(previousEventId, savedItemId);
        }
        if (nextEventId && nextEventId !== previousEventId) {
          await linkRedeemableToEvent(nextEventId, savedItemId);
        }
      } catch (linkErr) {
        console.warn('[RedeemableItemsAdmin] event link sync failed', linkErr && linkErr.message);
        toast.info('Item saved, but event link sync failed. You can retry by re-saving.');
      }
      toast.success(isNew ? 'Item created.' : 'Item updated.');
      setEditing(null);
    } catch (e) {
      console.error('[RedeemableItemsAdmin] save failed', e);
      toast.error(e.message || 'Failed to save item.');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (item) => {
    try {
      const ref = doc(firestore, 'businessListings', businessId, 'redeemableItems', item.id);
      await updateDoc(ref, { enabled: !item.enabled, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error('[RedeemableItemsAdmin] toggle failed', e);
      toast.error('Failed to toggle item.');
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      const ref = doc(firestore, 'businessListings', businessId, 'redeemableItems', id);
      await deleteDoc(ref);
      toast.info('Item deleted.');
    } catch (e) {
      console.error('[RedeemableItemsAdmin] delete failed', e);
      toast.error('Failed to delete item.');
    }
  };

  const sortedItems = useMemo(
    () => items.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)),
    [items],
  );

  // ----- Aggregate stats for summary banner -----
  const stats = useMemo(() => {
    let totalRedeemed = 0;
    let totalTokens = 0;
    let totalRefunded = 0;
    items.forEach((it) => {
      const consumed = it.consumedCount || 0;
      const refunded = it.refundedCount || 0;
      totalRedeemed += consumed;
      totalRefunded += refunded;
      totalTokens += consumed * (it.tokenCost || 0);
    });
    return { totalRedeemed, totalRefunded, totalTokens, itemCount: items.length };
  }, [items]);

  // ----- Refund flow -----
  const performRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      const fn = httpsCallable(functions, 'refundRedemption');
      await fn({ redemptionId: refundTarget.id, reason: refundReason.trim() || null });
      toast.success('Redemption refunded — tokens returned to member.');
      setRefundTarget(null);
      setRefundReason('');
    } catch (e) {
      console.error('[RedeemableItemsAdmin] refund failed', e);
      toast.error(e.message || 'Refund failed. Only owners or managers can refund.');
    } finally {
      setRefunding(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    const ms = ts.toMillis ? ts.toMillis() : ts;
    const d = new Date(ms);
    return d.toLocaleString();
  };
  const itemNameById = (id) => {
    const it = items.find((x) => x.id === id);
    return it ? it.name : '(deleted item)';
  };

  if (!businessId) {
    return (
      <div className="ria-empty">
        <FontAwesomeIcon icon={faExclamationTriangle} /> Select a business listing first.
      </div>
    );
  }

  return (
    <div className="ria">
      <div className="ria__header">
        <div>
          <h2><FontAwesomeIcon icon={faCoins} /> Redeemable Items</h2>
          <p className="ria__sub">
            Define what travellers can spend tokens on at this business.
            Disabled items don't appear to members but are kept for history.
          </p>
        </div>
        <button className="ria__new-btn" onClick={startNew}>
          <FontAwesomeIcon icon={faPlus} /> New item
        </button>
      </div>

      {loading ? (
        <div className="ria__loading">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading items…
        </div>
      ) : sortedItems.length === 0 ? (
        <div className="ria__empty">
          <p>No redeemable items yet.</p>
          <p className="ria__empty-hint">
            Add your first item to start accepting token redemptions.
          </p>
        </div>
      ) : (
        <>
          <div className="ria__summary" aria-label="Redemption summary">
            <div className="ria__summary-stat">
              <strong>{stats.totalRedeemed.toLocaleString()}</strong>
              <span>Items redeemed</span>
            </div>
            <div className="ria__summary-stat">
              <strong>{stats.totalTokens.toLocaleString()}</strong>
              <span>Tokens spent</span>
            </div>
            <div className="ria__summary-stat">
              <strong>{stats.totalRefunded.toLocaleString()}</strong>
              <span>Refunded items</span>
            </div>
            <div className="ria__summary-stat">
              <strong>{stats.itemCount}</strong>
              <span>Active items</span>
            </div>
          </div>
        <div className="ria__table">
          <div className="ria__row ria__row--head">
            <div>
              Item
              <HelpTip
                title="Item name &amp; description"
                body="What members see on your listing. Keep it short and specific."
                example="&quot;Free appetizer&quot; with a description like &quot;Choice of conch fritters or callaloo dip.&quot;"
              />
            </div>
            <div className="ria__num">
              Cost
              <HelpTip
                title="Token cost"
                body="How many tokens a member spends to redeem one of these items."
                example="3 tokens for an appetizer means a member with 5 tokens can redeem one and have 2 left."
              />
            </div>
            <div className="ria__num">
              Inventory
              <HelpTip
                title="Inventory remaining / total"
                body="How many of this item are still available out of the total you stocked. Shows ∞ if there is no limit."
                example="&quot;7 / 10&quot; means 3 have been claimed (reserved or redeemed) and 7 are left."
              />
            </div>
            <div className="ria__num">
              Per-user cap
              <HelpTip
                title="Max per user"
                body="The most times a single member can redeem this item — across the lifetime of the offer. Blank means unlimited."
                example="Set to 1 for a one-time welcome perk."
              />
            </div>
            <div className="ria__num">
              Redeemed
              <HelpTip
                title="Total redeemed"
                body="Number of times this item has been successfully redeemed and the tokens debited."
              />
            </div>
            <div>
              Confirm?
              <HelpTip
                title="Requires staff confirmation"
                body="&quot;Yes&quot; means a member starts a redemption from their phone, then a staff member must confirm it at the bar before tokens are spent. &quot;No&quot; means tokens are deducted instantly when the member taps Redeem."
                example="Use Yes for things you serve in person; No for things like a digital download."
              />
            </div>
            <div>
              Status
              <HelpTip
                title="Enabled status"
                body="Disabled items are hidden from members but kept for history. Click the pill to toggle."
              />
            </div>
            <div className="ria__actions-col">Actions</div>
          </div>
          {sortedItems.map((it) => {
            const remaining = (typeof it.totalInventory === 'number')
              ? Math.max(0, it.totalInventory - (it.reservedCount || 0) - (it.consumedCount || 0))
              : '∞';
            return (
              <div key={it.id} className={`ria__row ${it.enabled ? '' : 'ria__row--disabled'}`}>
                <div>
                  <div className="ria__item-name">{it.name}</div>
                  {it.description && (
                    <div className="ria__item-desc">{it.description}</div>
                  )}
                </div>
                <div className="ria__num"><FontAwesomeIcon icon={faCoins} /> {it.tokenCost}</div>
                <div className="ria__num">
                  {typeof it.totalInventory === 'number'
                    ? `${remaining} / ${it.totalInventory}`
                    : '∞'}
                </div>
                <div className="ria__num">
                  {typeof it.maxRedemptionsPerUser === 'number' ? it.maxRedemptionsPerUser : '∞'}
                </div>
                <div className="ria__num">{it.consumedCount || 0}</div>
                <div>{it.requiresStaffConfirmation ? 'Yes' : 'No'}</div>
                <div>
                  <button
                    className={`ria__toggle ${it.enabled ? 'ria__toggle--enabled' : 'ria__toggle--disabled'}`}
                    onClick={() => toggleEnabled(it)}
                    title={it.enabled ? 'Click to disable' : 'Click to enable'}
                  >
                    <FontAwesomeIcon icon={it.enabled ? faToggleOn : faToggleOff} />
                    {' '}{it.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                <div className="ria__actions-col">
                  <button className="ria__icon-btn" onClick={() => startEdit(it)} title="Edit">
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className="ria__icon-btn ria__icon-btn--danger"
                    onClick={() => setConfirmDelete(it)}
                    title="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* ---- Editor ---- */}
      {editing && (
        <div className="ria__editor-backdrop" onClick={cancelEdit}>
          <div className="ria__editor" onClick={(e) => e.stopPropagation()}>
            <h3>{editing.id ? 'Edit item' : 'New redeemable item'}</h3>

            <label className="ria__field">
              <span>
                Name
                <HelpTip
                  title="Item name"
                  body="Short, scannable name members will see in your listing."
                  example="&quot;Free appetizer&quot; or &quot;House cocktail&quot;"
                />
              </span>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setField({ name: e.target.value })}
                placeholder="e.g. Free appetizer"
                maxLength={120}
              />
            </label>

            <label className="ria__field">
              <span>
                Description
                <HelpTip
                  title="Description"
                  body="One or two sentences explaining what the member will receive."
                  example="&quot;Choice of conch fritters, callaloo dip, or breadfruit chips.&quot;"
                />
              </span>
              <textarea
                rows={2}
                value={editing.description || ''}
                onChange={(e) => setField({ description: e.target.value })}
                placeholder="Short description shown to travellers"
                maxLength={500}
              />
            </label>

            <div className="ria__field-row">
              <label className="ria__field">
                <span>
                  Token cost
                  <HelpTip
                    title="Token cost"
                    body="How many tokens the member spends each time they redeem this item."
                    example="3 tokens for a coffee; 10 tokens for a cocktail."
                  />
                </span>
                <input
                  type="number"
                  min="1"
                  value={editing.tokenCost}
                  onChange={(e) => setField({ tokenCost: Number(e.target.value) || 0 })}
                />
              </label>

              <label className="ria__field">
                <span>
                  Total inventory <em>(blank = unlimited)</em>
                  <HelpTip
                    title="Total inventory"
                    body="Hard cap on how many of this item can ever be redeemed. Leave blank for unlimited."
                    example="Set to 50 for a limited-time offer; leave blank for an always-on perk."
                  />
                </span>
                <input
                  type="number"
                  min="0"
                  value={editing.totalInventory === null ? '' : editing.totalInventory}
                  onChange={(e) => setField({
                    totalInventory: e.target.value === '' ? null : Number(e.target.value),
                  })}
                />
              </label>

              <label className="ria__field">
                <span>
                  Max per user <em>(blank = unlimited)</em>
                  <HelpTip
                    title="Max redemptions per user"
                    body="Limits how many times one member can redeem this. Blank means no limit."
                    example="Set to 1 for a one-time welcome perk so a member can’t claim it twice."
                  />
                </span>
                <input
                  type="number"
                  min="0"
                  value={editing.maxRedemptionsPerUser === null ? '' : editing.maxRedemptionsPerUser}
                  onChange={(e) => setField({
                    maxRedemptionsPerUser: e.target.value === '' ? null : Number(e.target.value),
                  })}
                />
              </label>
            </div>

            <div className="ria__field-row">
              <label className="ria__field">
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} /> Visible from
                  <HelpTip
                    title="Visible from"
                    body="The earliest date/time members will see this item. Leave blank to make it visible immediately."
                    example="Set to next Friday at 5pm to schedule a happy-hour perk in advance."
                  />
                </span>
                <input
                  type="datetime-local"
                  value={tsToInputValue(editing.visibleFrom)}
                  onChange={(e) => setField({ visibleFrom: inputValueToTs(e.target.value) })}
                />
              </label>
              <label className="ria__field">
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} /> Visible until
                  <HelpTip
                    title="Visible until"
                    body="The latest date/time members can see / redeem this item. Leave blank for no end date."
                    example="Set to the end of a holiday weekend to auto-retire a special."
                  />
                </span>
                <input
                  type="datetime-local"
                  value={tsToInputValue(editing.visibleUntil)}
                  onChange={(e) => setField({ visibleUntil: inputValueToTs(e.target.value) })}
                />
              </label>
              <label className="ria__field">
                <span>
                  Display order
                  <HelpTip
                    title="Display order"
                    body="Lower numbers appear first in the members’ list. Use to feature your most popular item at the top."
                    example="0 for the headline offer, 10/20/30 for the rest."
                  />
                </span>
                <input
                  type="number"
                  value={editing.displayOrder || 0}
                  onChange={(e) => setField({ displayOrder: Number(e.target.value) || 0 })}
                />
              </label>
            </div>

            <div className="ria__field-row">
              <label className="ria__field" style={{ flex: 1 }}>
                <span>
                  <FontAwesomeIcon icon={faCalendarAlt} /> Link to event (optional)
                  <HelpTip
                    title="Event-linked offer"
                    body="When set, this redeemable can ONLY be claimed during the event window (plus the event's grace minutes). Useful for happy-hour / show-only specials."
                    example="Link a 'free draft beer' item to a weekly Trivia Night so it auto-activates each Thursday."
                  />
                </span>
                <select
                  value={editing.eventId || ''}
                  onChange={(e) => setField({ eventId: e.target.value || null })}
                >
                  <option value="">— not linked —</option>
                  {businessEvents
                    .filter((ev) => ev.status !== 'cancelled')
                    .map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title} ({ev.status})
                      </option>
                    ))}
                </select>
              </label>
            </div>

            <div className="ria__check-row">
              <label className="ria__check">
                <input
                  type="checkbox"
                  checked={!!editing.enabled}
                  onChange={(e) => setField({ enabled: e.target.checked })}
                />
                Enabled (visible to members)
                <HelpTip
                  title="Enabled"
                  body="Uncheck to hide this item from members without deleting it. Past redemptions are kept for history."
                />
              </label>
              <label className="ria__check">
                <input
                  type="checkbox"
                  checked={!!editing.requiresStaffConfirmation}
                  onChange={(e) => setField({ requiresStaffConfirmation: e.target.checked })}
                />
                Requires staff confirmation at the bar
                <HelpTip
                  title="Staff confirmation"
                  body="When checked, the member starts a redemption that staff must confirm in person. When unchecked, tokens are deducted instantly when the member taps Redeem."
                  example="Use staff confirmation for in-person items (food, drink). Skip it for digital perks."
                />
              </label>
            </div>

            <div className="ria__editor-actions">
              <button className="ria__btn ria__btn--ghost" onClick={cancelEdit} disabled={saving}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
              <button className="ria__btn ria__btn--primary" onClick={save} disabled={saving}>
                {saving
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                  : <><FontAwesomeIcon icon={faSave} /> Save item</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Delete this item?"
        message={confirmDelete
          ? `"${confirmDelete.name}" will be permanently removed. Past redemptions will keep their record but the item won't be selectable for new redemptions.`
          : ''}
        confirmText="Delete"
        cancelText="Keep"
        type="danger"
        onConfirm={performDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* ---- Recent redemptions + refund ---- */}
      {!loading && (
        <div className="ria__recent">
          <h3>
            <FontAwesomeIcon icon={faClockRotateLeft} /> Recent redemptions
            <HelpTip
              className="ria__helptip--top"
              title="Recent redemptions"
              body="The last 25 confirmed and refunded redemptions for this listing. Owners and managers can refund here — tokens are returned to the member and they receive an email receipt."
            />
          </h3>
          {recentLoading ? (
            <div className="ria__recent-empty">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading…
            </div>
          ) : recent.length === 0 ? (
            <div className="ria__recent-empty">No redemptions yet.</div>
          ) : (
            <>
              <div className="ria__recent-row ria__recent-row--head">
                <div>Member</div>
                <div>Item</div>
                <div>Cost</div>
                <div>When</div>
                <div>Action</div>
              </div>
              {recent.map((r) => (
                <div
                  key={r.id}
                  className={`ria__recent-row ${r.status === 'refunded' ? 'ria__recent-row--refunded' : ''}`}
                >
                  <div>{r.memberNumber || '—'}</div>
                  <div>{itemNameById(r.redeemableItemId)}</div>
                  <div><FontAwesomeIcon icon={faCoins} /> {r.tokenCost}</div>
                  <div>{formatTime(r.confirmedAt)}</div>
                  <div>
                    {r.status === 'refunded' ? (
                      <span className="ria__recent-status ria__recent-status--refunded">
                        <FontAwesomeIcon icon={faCheckCircle} /> Refunded
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="ria__refund-btn"
                        onClick={() => { setRefundTarget(r); setRefundReason(''); }}
                        title="Refund tokens to member"
                      >
                        <FontAwesomeIcon icon={faUndo} /> Refund
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {refundTarget && (
        <div className="ria__editor-backdrop" onClick={() => !refunding && setRefundTarget(null)}>
          <div className="ria__editor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h3>Refund this redemption?</h3>
            <p style={{ margin: 0, fontSize: '.95rem', color: '#444' }}>
              <strong>{itemNameById(refundTarget.redeemableItemId)}</strong>
              {' — '}
              {refundTarget.tokenCost} token{refundTarget.tokenCost !== 1 ? 's' : ''} will be returned to
              member <strong>{refundTarget.memberNumber}</strong>. They will receive an email receipt.
            </p>
            <label className="ria__field">
              <span>Reason <em>(optional, shown internally)</em></span>
              <input
                className="ria__refund-input"
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="e.g. Item was out of stock"
                maxLength={200}
                autoFocus
              />
            </label>
            <div className="ria__editor-actions">
              <button
                className="ria__btn ria__btn--ghost"
                onClick={() => setRefundTarget(null)}
                disabled={refunding}
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
              <button
                className="ria__btn ria__btn--primary"
                onClick={performRefund}
                disabled={refunding}
                style={{ background: '#dc2626' }}
              >
                {refunding
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Refunding…</>
                  : <><FontAwesomeIcon icon={faUndo} /> Refund tokens</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RedeemableItemsAdmin;
